import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { openUserProfile } from 'features/users/openUserProfile';
import { ChevronDown, ChevronUp, Search, X } from 'lucide-react';
import axiosInstance from 'shared/services/api/axiosInstance';
import TrodoLoader from 'shared/components/ui/TrodoLoader';
import { scoreToPct, LevelBadge, startedFields } from 'features/agent-analytics/components/runCells';
import { KindPill } from 'features/agent-analytics/components/spanKind';
import DrawerScrim, { DRAWER_OVERLAY_CLASS } from 'shared/components/ui/DrawerScrim';
import TracePanel from 'features/agent-analytics/components/TracePanel';
import RunRightTabs from 'features/agent-analytics/components/RunRightTabs';
import CapabilityDrawer from 'features/agent-analytics/components/CapabilityDrawer';
import UsageHoverCard from 'features/agent-analytics/components/UsageBreakdown';
import RunSearchResults from 'features/agent-analytics/components/RunSearchResults';
import AskLucidButton from 'features/agent-analytics/components/AskLucidButton';
import AnchoredDropdown from 'features/agent-analytics/components/AnchoredDropdown';
import { useLinkedIssues, linkedIssuesCell, useOpenIssue } from 'features/agent-analytics/components/LinkedIssues';
import DetailHoverCard from 'shared/components/ui/DetailHoverCard';
import SpanEvaluateMenu from 'features/agent-analytics/components/SpanEvaluateMenu';
import { trackClientEvent } from 'shared/utils/trodoEvents';
import PromptRefLink from 'features/agent-analytics/components/PromptRefLink';

function formatDuration(ms) {
  if (ms == null) return '—';
  const v = Number(ms);
  if (!Number.isFinite(v)) return '—';
  if (v < 1000) return `${Math.round(v)} ms`;
  return `${(v / 1000).toFixed(2)} s`;
}

function formatCost(v) {
  if (v == null) return '—';
  const n = Number(v);
  if (!Number.isFinite(n)) return '—';
  if (n === 0) return '$0';
  if (n < 0.01) return `$${n.toFixed(4)}`;
  return `$${n.toFixed(2)}`;
}

/**
 * Slide-in drawer showing full run detail. Body is a left/right split:
 *   left  → trace (tree or timeline) with span-level click selection
 *   right → tabs: Activity | Scores | Conversations | Annotations
 * Activity reacts to the selected span: run-level when none is picked,
 * span-level when one is.
 */
export default function AgentRunDetailPage({
  runId: initialRunId,
  teamId,
  /** When opening from an issue member / deep-link, pre-select this span after trace loads. */
  initialSpanId,
  onClose,
  onChange,
  /** Optional prev/next navigation to step through the run list without closing. */
  onPrev,
  onNext,
  hasPrev = false,
  hasNext = false,
  /**
   * Which right-rail tab to land on. The grading queue opens straight to
   * 'scores' — arriving from a queue of things to grade and landing on
   * Activity means one pointless click every single time.
   */
  initialTab,
  /**
   * The query that led here (global search). Seeds the in-run search box and
   * an immediate run-level highlight, so the value whose match opened this
   * run is marked on arrival instead of the user re-finding it by eye.
   */
  initialQuery,
  /** When true, fills the parent panel instead of docking to the viewport right edge. */
  contained = false,
  /**
   * Whether clicking the dimmed area closes the drawer.
   *
   * False when a live list sits behind it — the runs table swaps the drawer to
   * whichever run you click, so a scrim that intercepted those clicks would
   * close the drawer on every attempt to move to the next run. Esc and the
   * close button still dismiss.
   */
  scrimDismisses = true,
}) {
  const [activeRunId, setActiveRunId] = useState(initialRunId);
  useEffect(() => { setActiveRunId(initialRunId); }, [initialRunId]);


  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [run, setRun] = useState(null);
  const [feedback, setFeedback] = useState([]);
  const [conversationRuns, setConversationRuns] = useState([]);
  const [capabilities, setCapabilities] = useState([]);
  const [conversationStats, setConversationStats] = useState(null);

  // Trace is lazy-but-auto: null = "not loaded yet", [] = "fetched, empty".
  const [spans, setSpans] = useState(null);
  const [spansLoading, setSpansLoading] = useState(false);

  const [selectedSpanId, setSelectedSpanId] = useState(null);
  // The trace-tree spans (listForRun) carry only a 2000-char preview of
  // input/output for performance; the full untruncated body is fetched on
  // demand when a span is selected (mirrors the Traces page).
  const [fullSpan, setFullSpan] = useState(null);
  const [activeTab, setActiveTab] = useState(initialTab || 'activity');
  useEffect(() => { setActiveTab(initialTab || 'activity'); }, [initialTab, initialRunId]);
  // Free-text search over this run + its spans (input/output/attributes).
  // Drives the trace filter (instant, client) AND a backend results dropdown
  // (keyword + semantic). Cleared when the run changes.
  const [query, setQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchMode, setSearchMode] = useState('empty');
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  // The stems the backend matched on, kept so a picked result can highlight
  // the words that actually contributed rather than the raw query string.
  const [searchTerms, setSearchTerms] = useState([]);
  /** Trace-scoped Ask messenger (docked bottom-right, portalled to body). */


  // Esc closes the drawer. The span drawer (TraceDetailPage) has always done
  // this; the run drawer never did, so the same gesture dismissed one
  // slide-over and did nothing on the other.
  //
  // Peels one layer at a time: while the search box or Ask is open, Esc
  // belongs to them. The search input has its own Esc handler and does not
  // stop propagation, so without this guard one keypress would close the
  // dropdown AND the whole drawer behind it.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      if (searchOpen) return;
      onClose?.();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose, searchOpen]);
  // Active highlight: { query, terms, passage, field, spanId, ts } — set when
  // a result is picked. terms mark every contributing word; passage is the
  // located sentence and becomes the spotlight target.
  const [highlight, setHighlight] = useState(null);
  useEffect(() => {
    setQuery(''); setSearchResults([]); setSearchOpen(false); setHighlight(null); setSearchTerms([]);
  }, [activeRunId]);

  // Arriving from global search: seed the search box and light the run-level
  // highlight straight away. Declared after the clear-on-run-change effect so
  // that on the opening render the seed wins the ordering. The results
  // dropdown is told NOT to auto-open for the seeded query — the highlight
  // already shows where the match is; a dropdown on arrival would cover it.
  const suppressSearchOpenRef = useRef(false);
  useEffect(() => {
    const q = (initialQuery || '').trim();
    if (!q || activeRunId !== initialRunId) return;
    suppressSearchOpenRef.current = true;
    setQuery(q);
    setHighlight({ query: q, field: null, spanId: null, ts: Date.now() });
  }, [initialQuery, initialRunId, activeRunId]);

  // Debounced backend search (keyword + semantic over embeddings).
  useEffect(() => {
    const q = query.trim();
    if (!activeRunId || !teamId || q.length < 2) {
      setSearchResults([]); setSearchMode('empty'); setSearchLoading(false); setSearchOpen(false);
      return undefined;
    }
    let cancelled = false;
    // Abort superseded requests rather than only ignoring their replies: while
    // someone types a phrase this fires several times, and a search over a
    // large run is real work on the server that nobody is waiting for any more.
    const controller = new AbortController();
    setSearchLoading(true);
    if (suppressSearchOpenRef.current) {
      suppressSearchOpenRef.current = false;
    } else {
      setSearchOpen(true);
    }
    const t = setTimeout(async () => {
      try {
        const { data } = await axiosInstance.get(
          `/agent-runs/${encodeURIComponent(activeRunId)}/search`,
          { params: { team_id: teamId, q, mode: 'auto' }, signal: controller.signal },
        );
        if (cancelled) return;
        setSearchResults(data?.results || []);
        setSearchMode(data?.mode || 'keyword');
        setSearchTerms(data?.stems?.length ? data.stems : (data?.terms || []));
      } catch {
        if (!cancelled) { setSearchResults([]); setSearchMode('keyword'); setSearchTerms([]); }
      } finally {
        if (!cancelled) setSearchLoading(false);
      }
    }, 250);
    return () => { cancelled = true; clearTimeout(t); controller.abort(); };
  }, [query, activeRunId, teamId]);

  // Which spans an eval's verdict matched ('run' for the run's own and its
  // conversation's), so the tree keeps them.
  const scoredIds = useMemo(() => {
    const evals = searchResults.filter((r) => r.matchType === 'eval');
    return evals.length ? new Set(evals.map((r) => (r.spanId ? String(r.spanId) : 'run'))) : null;
  }, [searchResults]);

  const pickSearchResult = useCallback(async (r) => {
    setSearchOpen(false);
    // An eval's verdict opens where it is shown: the Scores tab, on its span.
    if (r.matchType === 'eval') {
      setSelectedSpanId(r.spanId || null);
      setActiveTab('scores');
      return;
    }
    setActiveTab('activity');
    setSelectedSpanId(r.spanId || null);
    const q = query.trim();
    // Mark the contributing words immediately so the destination is never
    // blank while the passage is being located.
    setHighlight({
      query: q, terms: searchTerms, passage: '',
      field: r.field, spanId: r.spanId || null, ts: Date.now(),
    });
    try {
      const { data } = await axiosInstance.get(
        `/agent-runs/${encodeURIComponent(activeRunId)}/search/passage`,
        { params: { team_id: teamId, q, field: r.field, span_id: r.spanId || undefined } },
      );
      if (!data?.passage) return;
      // The exact stretch of text that explains this hit — the spotlight
      // target. For a semantic result this is the only thing that can be
      // highlighted at all, since the query words are not in the text.
      setHighlight({
        query: q,
        terms: data.stems?.length ? data.stems : searchTerms,
        passage: data.passage,
        field: r.field, spanId: r.spanId || null, ts: Date.now(),
      });
    } catch {
      // Keep the term-level highlight already on screen.
    }
  }, [query, searchTerms, activeRunId, teamId]);

  // Close the results dropdown when clicking outside the search box.
  const searchBoxRef = useRef(null);
  useEffect(() => {
    if (!searchOpen) return undefined;
    const onDown = (e) => {
      if (searchBoxRef.current && !searchBoxRef.current.contains(e.target)) setSearchOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [searchOpen]);

  const loadDetail = useCallback(async () => {
    if (!activeRunId || !teamId) return;
    setLoading(true);
    setError(null);
    setSpans(null);
    setSelectedSpanId(null);
    try {
      const { data } = await axiosInstance.get(
        `/agent-runs/${encodeURIComponent(activeRunId)}`,
        { params: { team_id: teamId } }
      );
      setRun(data?.run || null);
      setFeedback(data?.feedback || []);
      setConversationRuns(data?.conversationRuns || []);
      // The capability model is the source of truth. Normalise it to the cell's
      // shape; fall back to the legacy use-case clusters only for old runs
      // ingested before the capability model (so they still show something).
      const caps = (data?.capabilities || []).map((c) => ({
        key: c.id,
        id: c.id,
        name: c.name,
        confidence: c.confidence,
        subject_facet: c.subject_facet,
        definition: c.definition,
        run_count: c.run_count,
        // capability-level satisfaction (matches the Capabilities page drawer),
        // falling back to this run's score for the capability.
        satisfaction_score: c.capability_satisfaction ?? c.satisfaction_score,
        analysis: c.analysis,
      }));
      const legacyCaps = (data?.useCases || []).map((u) => ({
        key: `legacy-${u.cluster_id}`,
        name: u.label || `Cluster #${u.cluster_id}`,
        confidence: u.similarity,
      }));
      setCapabilities(caps.length ? caps : legacyCaps);
      setConversationStats(data?.conversationStats || null);
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Failed to load run.');
    } finally {
      setLoading(false);
    }
  }, [activeRunId, teamId]);

  useEffect(() => { loadDetail(); }, [loadDetail]);

  const loadSpans = useCallback(async () => {
    if (!activeRunId || !teamId) return;
    setSpansLoading(true);
    try {
      const { data } = await axiosInstance.get(
        `/agent-runs/${encodeURIComponent(activeRunId)}/spans`,
        { params: { team_id: teamId } }
      );
      setSpans(data?.spans || []);
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Failed to load trace.');
    } finally {
      setSpansLoading(false);
    }
  }, [activeRunId, teamId]);

  useEffect(() => {
    if (run && spans == null && !spansLoading) loadSpans();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [run]);

  useEffect(() => {
    if (!initialSpanId || !spans?.length) return;
    if (spans.some((s) => s.span_id === initialSpanId)) setSelectedSpanId(initialSpanId);
  }, [activeRunId, initialSpanId, spans]);

  const submitFeedback = async (payload) => {
    trackClientEvent(
      'agent_run_feedback_submitted',
      {
        run_id: activeRunId,
        feedback_kind: payload?.kind ?? null,
        rating: payload?.rating ?? null,
        has_comment: !!payload?.comment,
      },
      { category: 'product' }
    );
    await axiosInstance.post(
      `/sdk/runs/${encodeURIComponent(activeRunId)}/feedback`,
      { ...payload, distinct_id: run?.distinct_id || null, attributes: {} }
    );
    // Refresh the feedback (and the run's latest values) only: a full reload
    // would drop the span the feedback was just given on.
    const { data } = await axiosInstance.get(
      `/agent-runs/${encodeURIComponent(activeRunId)}`,
      { params: { team_id: teamId } }
    );
    setFeedback(data?.feedback || []);
    if (data?.run) setRun(data.run);
    onChange?.();
  };

  // Fetch the FULL span (untruncated input/output, full attributes/usage/cost)
  // when a span is selected. The tree row renders instantly from its preview;
  // this merges the complete body in once it arrives.
  useEffect(() => {
    if (!selectedSpanId || !teamId) { setFullSpan(null); return; }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axiosInstance.get(
          `/agent-spans/${encodeURIComponent(selectedSpanId)}`,
          { params: { team_id: teamId } }
        );
        if (!cancelled) setFullSpan(data?.span || null);
      } catch {
        if (!cancelled) setFullSpan(null);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedSpanId, teamId]);

  const selectedSpan = useMemo(() => {
    if (!selectedSpanId || !spans) return null;
    const base = spans.find((s) => s.span_id === selectedSpanId) || null;
    if (!base) return null;
    // Prefer the full fetched span (untruncated input/output, full
    // attributes/usage/cost) but fall back to the tree preview while it loads,
    // so the panel never shows a 2000-char-cut payload.
    if (fullSpan && fullSpan.span_id === selectedSpanId) {
      return { ...base, ...fullSpan };
    }
    return base;
  }, [selectedSpanId, spans, fullSpan]);

  const openRunFromPayload = useCallback((runId) => {
    if (!runId) return;
    setActiveRunId(String(runId));
    setActiveTab('activity');
  }, []);

  const openSpanFromPayload = useCallback((spanId) => {
    if (!spanId) return;
    if (spans?.some((s) => s.span_id === spanId)) {
      setSelectedSpanId(spanId);
      setActiveTab('activity');
    }
  }, [spans]);

  const openAnnotationContext = useCallback((ctx) => {
    if (!ctx || ctx.type === 'conversation') return;
    if (ctx.type === 'run') {
      if (ctx.id && String(ctx.id) !== String(activeRunId)) {
        setActiveRunId(String(ctx.id));
      }
      setSelectedSpanId(null);
      setActiveTab('activity');
      return;
    }
    if (ctx.type === 'span' && ctx.id) {
      setSelectedSpanId(ctx.id);
      setActiveTab('activity');
    }
  }, [activeRunId]);

  // Drag-to-resize the popup width. The panel is anchored to the right, so the
  // width is the distance from the cursor to the right edge of the viewport.
  // null = use the default responsive width.
  const [panelWidth, setPanelWidth] = useState(null);
  const startResize = useCallback((e) => {
    e.preventDefault();
    const onMove = (ev) => {
      const w = window.innerWidth - ev.clientX;
      const max = Math.round(window.innerWidth * 0.98);
      setPanelWidth(Math.max(420, Math.min(max, w)));
    };
    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'ew-resize';
  }, []);

  return (
    <div
      className={
        contained
          ? 'pointer-events-auto absolute inset-0 z-[50]'
          : DRAWER_OVERLAY_CLASS
      }
      role="region"
      aria-label="Run detail"
    >
      {/* No scrim in `contained` mode: there it fills a parent panel
          (IssueDetailPage embeds it) and there is nothing behind to dim. */}
      {!contained ? <DrawerScrim onClose={onClose} dismisses={scrimDismisses} /> : null}
      <section
        className={
          contained
            ? 'pointer-events-auto relative flex h-full w-full flex-col overflow-hidden bg-surface'
            : `pointer-events-auto relative flex h-full flex-col overflow-hidden border-l border-t border-[color:var(--border-default)] bg-surface shadow-2xl ${panelWidth == null ? 'w-[min(92%,1000px)]' : 'max-w-full'}`
        }
        style={{ fontFamily: 'Inter, sans-serif', width: contained || panelWidth == null ? undefined : `${panelWidth}px` }}
      >
        {!contained ? (
          <div
            onMouseDown={startResize}
            onDoubleClick={() => setPanelWidth(null)}
            title="Drag to resize · double-click to reset"
            className="group absolute left-0 top-0 z-[60] h-full w-1.5 cursor-ew-resize hover:bg-[color:var(--trodo-primary)]/40"
            role="separator"
            aria-orientation="vertical"
          />
        ) : null}

        {loading && !run ? (
          <div className="flex flex-1 items-center justify-center"><TrodoLoader label="Loading run" /></div>
        ) : error ? (
          <div className="m-4 rounded-lg border border-[color:var(--feedback-error-bg)] bg-[color:var(--feedback-error-bg)] p-3 text-[12px] text-[color:var(--feedback-error)]">
            {error}
          </div>
        ) : run ? (
          <div className="flex min-h-0 flex-1 flex-col gap-4 px-4 pt-4">
            {/* Control row: prev/next run · search · close */}
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={onPrev}
                  disabled={!hasPrev}
                  title="Previous run"
                  className="flex h-7 w-7 items-center justify-center rounded-md text-[color:var(--text-muted)] transition-colors hover:bg-surface-hover hover:text-[color:var(--text-primary)] disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  <ChevronUp size={16} />
                </button>
                <button
                  type="button"
                  onClick={onNext}
                  disabled={!hasNext}
                  title="Next run"
                  className="flex h-7 w-7 items-center justify-center rounded-md text-[color:var(--text-muted)] transition-colors hover:bg-surface-hover hover:text-[color:var(--text-primary)] disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  <ChevronDown size={16} />
                </button>
              </div>

              {/* Search this run + its spans (input/output/attributes; semantic + keyword) */}
              <div ref={searchBoxRef} className="relative min-w-0 flex-1">
                <Search size={14} className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-[color:var(--text-muted)]" />
                <input
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onFocus={() => { if (query.trim().length >= 2) setSearchOpen(true); }}
                  onKeyDown={(e) => { if (e.key === 'Escape') { setSearchOpen(false); e.currentTarget.blur(); } }}
                  placeholder="Search this run…"
                  className="h-8 w-full rounded-lg bg-[color:var(--surface-sunken)] pl-8 pr-7 text-[12px] text-[color:var(--text-primary)] placeholder:text-[color:var(--text-muted)] focus:outline-none focus:ring-1 focus:ring-[color:var(--trodo-primary)]"
                />
                {query ? (
                  <button
                    type="button"
                    onClick={() => setQuery('')}
                    aria-label="Clear search"
                    className="absolute right-1.5 top-1/2 flex h-5 w-5 -translate-y-1/2 items-center justify-center rounded text-[color:var(--text-muted)] hover:bg-surface-hover hover:text-[color:var(--text-primary)]"
                  >
                    <X size={13} />
                  </button>
                ) : null}
                {searchOpen && query.trim().length >= 2 ? (
                  <RunSearchResults
                    results={searchResults}
                    terms={searchTerms}
                    loading={searchLoading}
                    query={query.trim()}
                    mode={searchMode}
                    onPick={pickSearchResult}
                  />
                ) : null}
              </div>

              {/* Follows the tree: the run, or the span selected in it. */}
              <AskLucidButton runId={run?.run_id} spanId={selectedSpan?.span_id} />

              <button
                type="button"
                onClick={onClose}
                aria-label="Close"
                className="flex h-7 w-7 flex-none items-center justify-center rounded-md text-[color:var(--text-muted)] transition-colors hover:bg-surface-hover hover:text-[color:var(--text-primary)]"
              >
                <X size={16} />
              </button>
            </div>
            {selectedSpan
              ? <SpanMetaTags span={selectedSpan} teamId={teamId} onExit={() => setSelectedSpanId(null)} />
              : <RunMetaTags run={run} teamId={teamId} spans={spans} capabilities={capabilities} conversationStats={conversationStats} onOpenRun={setActiveRunId} onOpenConversation={() => setActiveTab('conversations')} />}
            <div
              className="-mx-4 grid min-h-0 flex-1 gap-0 border-t border-[color:var(--border-default)]"
              // 2fr/3fr, not 40%/60%: percentages are of the container, so with
              // a 12px gap the columns summed to 100% + 12px and the right panel
              // hung 12px past the meta strip above it. fr splits what's left
              // after the gap, so both panels line up with the card.
              // minmax(0, …) stops a wide payload from widening its column.
              style={{ gridTemplateColumns: 'minmax(0, 2fr) minmax(0, 3fr)' }}
            >
              <TracePanel
                spans={spans}
                spansLoading={spansLoading}
                selectedSpanId={selectedSpanId}
                onSelectSpan={setSelectedSpanId}
                query={query}
                terms={searchTerms}
                scoredIds={scoredIds}
                run={run}
                className="min-h-0"
              />
              <RunRightTabs
                teamId={teamId}
                activeTab={activeTab}
                onChangeTab={setActiveTab}
                run={run}
                selectedSpan={selectedSpan}
                onClearSpan={() => setSelectedSpanId(null)}
                feedback={feedback}
                onSubmitFeedback={submitFeedback}
                conversationRuns={conversationRuns}
                activeRunId={activeRunId}
                onSelectRun={setActiveRunId}
                onRunClick={openRunFromPayload}
                onSpanClick={openSpanFromPayload}
                onOpenContext={openAnnotationContext}
                highlight={highlight}
                className="min-h-0 border-l border-[color:var(--border-default)]"
              />
            </div>
          </div>
        ) : null}
      </section>

    </div>
  );
}

/** In-page overlay for a single use-case's detail. */
/** One cell in the meta grid — label on top, value below. */
function MetaCell({ label, children, wide = false }) {
  return (
    <div className={`flex min-w-0 flex-col gap-1 ${wide ? 'col-span-2' : ''}`}>
      <span className="text-[11px] text-[color:var(--text-muted)]">{label}</span>
      <div className="truncate text-[13px] text-[color:var(--text-primary)]">{children}</div>
    </div>
  );
}

/** Coloured status pill matching the trace screenshot. */
function StatusPillInline({ status }) {
  const map = {
    ok:      { bg: 'var(--feedback-success-bg)', fg: 'var(--feedback-success)', label: 'OK' },
    error:   { bg: 'var(--feedback-error-bg)', fg: 'var(--feedback-error)', label: 'Error' },
    running: { bg: 'var(--feedback-info-bg)', fg: 'var(--feedback-info)', label: 'Running' },
  };
  const s = map[status] || { bg: 'var(--surface-hover)', fg: 'var(--text-secondary)', label: status || '—' };
  return (
    <span
      className="inline-flex items-center rounded-md px-2 py-0.5 text-[12px] font-medium"
      style={{ backgroundColor: s.bg, color: s.fg }}
    >
      {s.label}
    </span>
  );
}

// Span-kind label + colour now come from spanKind.js. This file used to carry
// its own map that disagreed with the spans table's on two kinds and hardcoded
// two hexes that never flipped for dark mode.

/** Span meta strip — replaces the run meta strip while a span is selected.
 *  Shows the span's own properties + an "Exit span" button to return to run. */
function SpanMetaTags({ span, teamId, onExit }) {
  const linkedIssues = useLinkedIssues(teamId, 'span', span.span_id);
  const openIssue = useOpenIssue();
  const hasTokens = (span.input_tokens != null && span.input_tokens > 0)
    || (span.output_tokens != null && span.output_tokens > 0);
  const hasCost = span.cost != null && Number(span.cost) > 0;

  // The exact managed-prompt version THIS span ran. Prefer the full attributes
  // (merged in once the span detail loads); fall back to the tree row's narrow
  // prompt columns so the cell is right immediately rather than after a
  // round-trip.
  const promptName = span.attributes?.['trodo.prompt.name'] || span.prompt_name || null;
  const promptHash = span.attributes?.['trodo.prompt.version_hash'] || span.prompt_version_hash || null;

  const cells = [
    promptName && {
      label: 'Prompt',
      content: <PromptRefLink name={String(promptName)} versionHash={promptHash} />,
    },
    span.status && {
      label: 'Status',
      content: (
        <span className="inline-flex flex-wrap items-center gap-2">
          <StatusPillInline status={span.status} />
          <LevelBadge level={span.level} status={span.status} />
        </span>
      ),
    },
    span.duration_ms != null && { label: 'Duration', content: <span>{formatDuration(span.duration_ms)}</span> },
    hasTokens && {
      label: 'Tokens',
      content: (
        <UsageHoverCard spans={[span]} mode="tokens">
          <span className="tabular-nums" title="in / out">{Number(span.input_tokens || 0).toLocaleString()} in · {Number(span.output_tokens || 0).toLocaleString()} out</span>
        </UsageHoverCard>
      ),
    },
    hasCost && {
      label: 'Cost',
      content: (
        <UsageHoverCard spans={[span]} mode="cost">
          <span className="tabular-nums">{formatCost(span.cost)}</span>
        </UsageHoverCard>
      ),
    },
    span.model && { label: 'Model', content: <span className="font-mono">{span.model}</span> },
    span.provider && { label: 'Provider', content: <span>{span.provider}</span> },
    span.tool_name && { label: 'Tool', content: <span className="font-mono">{span.tool_name}</span> },
    ...startedFields(span.started_at),
    linkedIssuesCell(linkedIssues, openIssue),
  ].filter(Boolean);

  return (
    <section style={{ fontFamily: 'Inter, sans-serif' }}>
      <div className="pb-1">
        <div className="mb-3 flex items-center gap-2">
          <KindPill kind={span.kind} />
          <span className="min-w-0 flex-1 truncate text-[13px] font-semibold text-[color:var(--text-primary)]" title={span.name || undefined}>
            {span.name || '(span)'}
          </span>
          {span.span_id && span.kind === 'llm' ? (
            <SpanEvaluateMenu
              spanId={span.span_id}
              hasPrompt={Boolean(span.attributes?.['trodo.prompt.version_hash'])}
              compact
            />
          ) : null}
          <button
            type="button"
            onClick={onExit}
            className="inline-flex flex-none items-center gap-1 rounded-md border border-[color:var(--border-default)] bg-surface px-2 py-1 text-[11px] text-[color:var(--text-secondary)] transition-colors hover:bg-surface-hover hover:text-[color:var(--text-primary)]"
          >
            <X size={13} /> Exit span
          </button>
        </div>
        {cells.length > 0 ? (
          <div className="grid gap-x-6 gap-y-4" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))' }}>
            {cells.map((cell, i) => (
              <MetaCell key={i} label={cell.label} wide={cell.wide}>{cell.content}</MetaCell>
            ))}
          </div>
        ) : null}
      </div>
    </section>
  );
}

function RunMetaTags({ run, teamId, spans = null, capabilities = [], conversationStats = null, onOpenRun, onOpenConversation }) {
  const [activeCapability, setActiveCapability] = useState(null);
  const linkedIssues = useLinkedIssues(teamId, 'run', run.run_id);
  const openIssue = useOpenIssue();

  const ragePct     = scoreToPct(run.rage_score);
  const satPct      = scoreToPct(run.satisfaction_score);
  const convRagePct = scoreToPct(conversationStats?.rage_score);
  const convSatPct  = scoreToPct(conversationStats?.satisfaction_score);
  const showConvScores = conversationStats != null && (convRagePct != null || convSatPct != null);

  const tokensIn  = run.total_tokens_in;
  const tokensOut = run.total_tokens_out;
  const hasTokens = (tokensIn != null && tokensIn > 0) || (tokensOut != null && tokensOut > 0);
  const hasCost   = run.total_cost != null && Number(run.total_cost) > 0;

  // Every managed prompt this run's spans used, deduped by the SDK.
  //
  // Framed as a SUMMARY on purpose: prompts belong to spans, and one run's
  // spans routinely use different prompts, different versions, or none. This
  // says "these appeared somewhere in this run" — the trace tree marks WHICH
  // span used which, and that is the precise answer.
  const runPrompts = Array.isArray(run.attributes?.['trodo.prompts'])
    ? run.attributes['trodo.prompts']
    : [];

  // Build ordered cells — only include cells that have a value
  const cells = [
    run.agent_name   && { label: 'Agent',   content: <span>{run.agent_name}</span> },
    run.status       && { label: 'Status',  content: (
      <span className="inline-flex flex-wrap items-center gap-2">
        <StatusPillInline status={run.status} />
        <LevelBadge level={run.level} status={run.status} />
      </span>
    ) },
    runPrompts.length > 0 && {
      label: runPrompts.length === 1 ? 'Prompt used' : 'Prompts used',
      wide: true,
      content: (
        <span className="inline-flex flex-wrap items-center gap-x-3 gap-y-1">
          {runPrompts.map((p) => (
            <PromptRefLink
              key={`${p.name}@${p.version_hash || ''}`}
              name={p.name}
              versionHash={p.version_hash}
            />
          ))}
        </span>
      ),
    },
    run.duration_ms != null && { label: 'Duration', content: <span>{formatDuration(run.duration_ms)}</span> },
    ...startedFields(run.started_at),
    hasTokens        && {
      label: 'Tokens',
      content: (
        <UsageHoverCard spans={spans} mode="tokens">
          <span className="tabular-nums" title="in / out">{Number(tokensIn || 0).toLocaleString()} in · {Number(tokensOut || 0).toLocaleString()} out</span>
        </UsageHoverCard>
      ),
    },
    hasCost          && {
      label: 'Cost',
      content: (
        <UsageHoverCard spans={spans} mode="cost">
          <span className="tabular-nums">{formatCost(run.total_cost)}</span>
        </UsageHoverCard>
      ),
    },
    // The run's own score; the conversation's average is one hover away
    // rather than a "· session N%" tail that read as a second, unexplained
    // number (and called the conversation a "session").
    ragePct != null  && {
      label: 'Rage',
      content: (
        <DetailHoverCard
          title="Rage"
          rows={[{ label: 'This run', value: `${ragePct}%` }, showConvScores && convRagePct != null && { label: 'Conversation average', value: `${convRagePct}%` }]}
          className="tabular-nums"
        >
          <span className={ragePct >= 40 ? 'text-[color:var(--feedback-error)]' : undefined}>{ragePct}%</span>
        </DetailHoverCard>
      ),
    },
    satPct != null   && {
      label: 'Satisfaction',
      content: (
        <DetailHoverCard
          title="Satisfaction"
          rows={[{ label: 'This run', value: `${satPct}%` }, showConvScores && convSatPct != null && { label: 'Conversation average', value: `${convSatPct}%` }]}
          className="tabular-nums"
        >
          <span className={satPct <= 30 ? 'text-[color:var(--feedback-error)]' : satPct >= 70 ? 'text-[color:var(--feedback-success)]' : undefined}>{satPct}%</span>
        </DetailHoverCard>
      ),
    },
    run.distinct_id  && {
      label: 'User',
      content: (
        <button
          type="button"
          onClick={() => openUserProfile(run.distinct_id)}
          title={run.distinct_id}
          className="cursor-pointer border-0 bg-transparent p-0 font-mono text-[color:var(--text-primary)] underline-offset-2 hover:underline"
        >
          {String(run.distinct_id).length > 24 ? `${String(run.distinct_id).slice(0, 24)}…` : run.distinct_id}
        </button>
      ),
    },
    // The conversation this run belongs to (it used to be labelled "Session",
    // which nobody could place). Opens the Conversations tab.
    run.conversation_id && {
      label: 'Conversation',
      content: (
        <button
          type="button"
          onClick={onOpenConversation}
          title={String(run.conversation_id)}
          className="cursor-pointer border-0 bg-transparent p-0 font-mono text-[color:var(--text-primary)] underline-offset-2 hover:underline"
        >
          {String(run.conversation_id).length > 20 ? `${String(run.conversation_id).slice(0, 20)}…` : run.conversation_id}
        </button>
      ),
    },
    (capabilities.length > 0 || run.use_case_label) && {
      label: capabilities.length > 1 ? `Capabilities (${capabilities.length})` : 'Capability',
      wide: true,
      // Single capability → click the tag opens its drawer directly. Multiple →
      // a dropdown; clicking a row opens that capability's drawer (over the run
      // popup, on the side — same as the Capabilities page).
      content: capabilities.length > 1 ? (
        <AnchoredDropdown
          trigger={
            <button type="button" className="inline-flex items-center gap-0.5 text-left text-[13px] text-[color:var(--text-primary)] hover:underline underline-offset-2">
              {capabilities[0].name}
              <ChevronDown size={12} className="mt-px flex-none" />
            </button>
          }
        >
          {capabilities.map((cap) => (
            <button
              key={cap.key}
              type="button"
              onClick={() => setActiveCapability(cap)}
              className="flex w-full items-center gap-2 px-3 py-1.5 text-[11px] hover:bg-[color:var(--surface-sunken)]"
            >
              <span className="font-medium text-[color:var(--text-primary)]">{cap.name}</span>
              <span className="ml-auto text-[color:var(--text-muted)]">{Math.round((cap.confidence || 0) * 100)}%</span>
            </button>
          ))}
        </AnchoredDropdown>
      ) : capabilities.length === 1 ? (
        <button
          type="button"
          onClick={() => setActiveCapability(capabilities[0])}
          className="text-left text-[13px] text-[color:var(--text-primary)] hover:underline underline-offset-2"
        >
          {capabilities[0].name}
        </button>
      ) : (
        <span>{run.use_case_label}</span>
      ),
    },
    // The issues this run is evidence for, beside the capabilities it served.
    linkedIssuesCell(linkedIssues, openIssue),
  ].filter(Boolean);

  // The run's error is not repeated here: it leads the Activity tab, beside
  // the input and output it explains, rather than wedged under the meta grid.
  return (
    <section style={{ fontFamily: 'Inter, sans-serif' }}>
      {/* Meta strip: one card, 5-column grid */}
      <div className="pb-1">
        <div className="grid gap-x-6 gap-y-4" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))' }}>
          {cells.map((cell, i) => (
            <MetaCell key={i} label={cell.label} wide={cell.wide}>{cell.content}</MetaCell>
          ))}
        </div>
      </div>

      {activeCapability && (
        <CapabilityDrawer
          capability={activeCapability}
          agentName={run.agent_name}
          onClose={() => setActiveCapability(null)}
          onRunClick={onOpenRun ? (rid) => { if (rid) { setActiveCapability(null); onOpenRun(rid); } } : undefined}
        />
      )}
    </section>
  );
}
